import { ShareTicketBody } from "@mg/schemas/src/christo/catalyst";
import {
  FILE_SIZE_LIMITS,
  formatFileSize,
} from "@mg/schemas/src/commons/file-limits";
import { ArrowUp } from "@phosphor-icons/react";
import { Grid, Heading, IconButton } from "@radix-ui/themes";
import {
  useNavigate,
  useRouter,
  useSearch,
  useRouterState,
} from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { DeleteFolderDialog } from "./components/DeleteFolderDialog";
import { DeleteTicketDialog } from "./components/DeleteTicketDialog";
import {
  ShareDialog,
  type ShareDialogProps,
} from "./components/dialogs/ShareDialog";
import { DragAndDropFileUpload } from "./components/DragAndDropUpload";
import { EmptyTicketsView } from "./components/EmptyTicketsView";
import { InfiniteScrollTrigger } from "./components/InfiniteScrollTrigger";
import { NoTicketsFoundView } from "./components/NoTicketsFoundView";
import { TicketGrid } from "./components/TicketGrid";
import { TicketsHeader } from "./components/TicketsHeader";
import { TicketTable } from "./components/TicketTable";
import { RenameDialog } from "./components/UpdateNameDialog";
import { ticketsRoute } from "./route";
import { VersionManagerDialog } from "./routes/components/dialogs/VersionManagerDialog";
import { ticketRoute } from "./routes/ticket/route";

import { CreateFolderDialog } from "../../components/dialogs/CreateFolderDialog";
import { PageHeader } from "../../components/PageHeader";
import { useUI } from "../../contexts/ui";
import { useUploadProgress } from "../../hooks/useUploadProgress";
import { uploadFile } from "../../services/upload";
import { errorAnalyticsPayload } from "../../utils/analytics";
import { canUploadTickets, isPunttGuest } from "../../utils/auth";
import { batch } from "../../utils/batch";
import { DISALLOWED_FILES } from "../../utils/constants";
import { directoryNameToFolderStub, fileToTicketStub } from "../../utils/files";
import { isNil } from "../../utils/fp";
import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import { downloadAsset, errorMessage } from "../../utils/http";
import {
  useDownloadFolderLink,
  useShareFolder,
  useShareFolderLink,
} from "../../utils/queries/folders";
import {
  useCombineTickets,
  useCreateFolderMutation,
  useDeleteFolderMutation,
  useDeleteTicketMutation,
  useDownloadTicketLink,
  useProjectMutation,
  useEditProjectMutation,
  useRevisionMutation,
  useShareTicket,
  useShareTicketLink,
  useUpdateFolderMutation,
  useDeleteRevisionMutation,
  useGetTicketMutation,
} from "../../utils/queries/projects";
import {
  addTemporaryFolders,
  addTemporaryTickets,
  replaceTemporaryFolder,
  replaceTemporaryTicket,
  updateBreadcrumbName,
  updateTicketTitle,
} from "../../utils/slices/punttProjects";
import { resetTicket, setTicket } from "../../utils/slices/ticket";

export interface DragDropProps {
  selectedIds: string[];
  setSelectedIds: (ids: string[] | ((prev: string[]) => string[])) => void;
  setIsDraggingCards: (isDragging: boolean) => void;
  setDropTargetId: (id: string | undefined) => void;
  setLastSelectedId: (id: string | undefined) => void;
  lastSelectedId: string | undefined;
  isDraggingCards: boolean;
  isInProgress: Set<string>;
  isRemoving: Set<string>;
  dropTargetId: string | undefined;
  isModifierKeyPressed: boolean;
  onSelect: (id: string, e: React.MouseEvent) => void;
  onDragStart: (id: string, e: React.DragEvent) => void;
  onDragEnter: (id: string, e: React.DragEvent) => void;
  onDragLeave: (id: string, e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (id: string, e: React.DragEvent) => void;
}

export const MAX_SIZE_GB = FILE_SIZE_LIMITS.UPLOAD.MAX_SIZE_GB;

export function Tickets() {
  const { t } = useTranslation("ui");
  const dispatch = useAppDispatch();

  const tickets = useAppSelector((state) => state.punttProjects.tickets);
  const folders = useAppSelector((state) => state.punttProjects.folders);
  const more = useAppSelector((state) => state.punttProjects.more);
  const versions = useAppSelector((state) => state.ticket.revisions);

  const markedFolders = useMemo(
    () =>
      folders.map((f) => ({
        ...f,
        isFolder: true,
      })),
    [folders],
  );
  const mergedFoldersAndTickets = useMemo(
    () =>
      [...markedFolders, ...tickets].toSorted((a, b) => {
        const dateA = new Date(a.updatedAt as string);
        const dateB = new Date(b.updatedAt as string);

        // Sort in descending order (most recent first)
        return dateB.getTime() - dateA.getTime();
      }),
    [markedFolders, tickets],
  );

  const router = useRouter();
  const navigate = useNavigate({ from: ticketsRoute.to });
  const { folderId, view, participants, ticketTitle } = useSearch({
    from: ticketsRoute.id,
  });
  const posthog = usePostHog();
  const { notify } = useUI();

  const mutation = useDeleteTicketMutation();
  const ticketMutation = useProjectMutation();
  const revisionMutation = useRevisionMutation();
  const createFolderMutation = useCreateFolderMutation();
  const deleteFolderMutation = useDeleteFolderMutation();
  const updateFolderMutation = useUpdateFolderMutation();
  const folderShareLinkMutation = useShareFolderLink();
  const shareFolderMutation = useShareFolder();
  const ticketShareLinkMutation = useShareTicketLink();
  const shareTicketMutation = useShareTicket();
  const folderDownloadMutation = useDownloadFolderLink();
  const ticketDownloadMutation = useDownloadTicketLink();
  const combineTicketsMutation = useCombineTickets();
  const editProjectMutation = useEditProjectMutation();
  const [ticketId, setTicketId] = useState<string>();
  const deleteRevisionMutation = useDeleteRevisionMutation(ticketId!); // ticketId will be set any time this gets used
  const getTicketMutation = useGetTicketMutation();

  const [deletingTicketId, setDeletingTicketId] = useState<string>();
  const [deletingFolderId, setDeletingFolderId] = useState<string>();
  const [shareItem, setShareItem] = useState<ShareDialogProps["item"]>();
  const [shareLink, setShareLink] = useState<string>();
  const [renamingId, setRenamingId] = useState<string>();
  const filesUploadRef = useRef<HTMLInputElement>(null);
  const foldersUploadRef = useRef<HTMLInputElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [didSearch, setDidSearch] = useState(() =>
    Boolean(ticketTitle?.length || participants?.length),
  );
  const [versionManagerDialogOpen, setVersionManagerDialogOpen] =
    useState(false);

  // Upload progress state
  const {
    uploadState,
    startUpload,
    updateProgress,
    setError,
    completeUpload,
    cancelUpload,
  } = useUploadProgress();

  const routerState = useRouterState();
  const isLoading = routerState.status === "pending";

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [lastSelectedId, setLastSelectedId] = useState<string>();
  const [isDraggingCards, setIsDraggingCards] = useState(false);
  const [isInProgress, setIsInProgress] = useState<Set<string>>(new Set());
  const [isRemoving, setIsRemoving] = useState<Set<string>>(new Set());
  const [dropTargetId, setDropTargetId] = useState<string>();
  const [isModifierKeyPressed, setIsModifierKeyPressed] = useState(false);

  // Scroll to top functionality
  const SCROLL_THRESHOLD = 100;
  const SCROLL_ANIMATION_DURATION = 300;
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const articleRef = useRef<HTMLElement>(null);
  const animationIdRef = useRef<number>();

  const handleScroll = useCallback(() => {
    try {
      if (articleRef.current && articleRef.current.isConnected) {
        const scrollTop = articleRef.current.scrollTop;
        setShowScrollToTop(scrollTop > SCROLL_THRESHOLD);
      }
    } catch (error) {
      console.error("Error handling scroll:", error);
      setShowScrollToTop(false);
    }
  }, []);

  const scrollToTop = useCallback(() => {
    if (!articleRef.current) return;

    const element = articleRef.current;
    const start = element.scrollTop;
    const duration = SCROLL_ANIMATION_DURATION;
    const startTime = performance.now();

    const animateScroll = (now: number) => {
      try {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Use easing function for smoother animation
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);

        if (element && element.isConnected) {
          element.scrollTop = start * (1 - easeOutCubic);
        }

        if (progress < 1) {
          animationIdRef.current = requestAnimationFrame(animateScroll);
        }
      } catch (error) {
        console.error("Error during scroll animation:", error);
        // Fallback to instant scroll
        if (element && element.isConnected) {
          element.scrollTop = 0;
        }
      }
    };

    animationIdRef.current = requestAnimationFrame(animateScroll);
  }, []);

  useEffect(() => {
    const article = articleRef.current;
    if (article) {
      try {
        article.addEventListener("scroll", handleScroll);
        return () => {
          try {
            article.removeEventListener("scroll", handleScroll);
            // Clean up any ongoing animation
            if (animationIdRef.current) {
              cancelAnimationFrame(animationIdRef.current);
            }
          } catch (error) {
            console.error("Error removing scroll listener:", error);
          }
        };
      } catch (error) {
        console.error("Error adding scroll listener:", error);
      }
    }
  }, [handleScroll]);

  const handleSelect = (id: string, e: React.MouseEvent) => {
    const initialSelectionCount = selectedIds.length;
    if (!isInProgress.has(id) && !isRemoving.has(id)) {
      if (e.metaKey || e.ctrlKey) {
        setSelectedIds((prev) =>
          prev.includes(id)
            ? prev.filter((itemId) => itemId !== id)
            : [...prev, id],
        );
        setLastSelectedId(id);
      } else if (e.shiftKey && lastSelectedId) {
        const allIds = mergedFoldersAndTickets.map((item) => item._id);
        const startIdx = allIds.indexOf(lastSelectedId);
        const endIdx = allIds.indexOf(id);
        const range = allIds
          .filter((v) => !isInProgress.has(v) && !isRemoving.has(v))
          .slice(Math.min(startIdx, endIdx), Math.max(startIdx, endIdx) + 1);
        setSelectedIds(range);
        setLastSelectedId(id);
      } else {
        setSelectedIds([id]);
        setLastSelectedId(id);
      }
      const finalSelectionCount = selectedIds.length;
      posthog.capture("clicked_tickets_card", {
        id: id,
        is_selectable: !isInProgress.has(id) && !isRemoving.has(id),
        add_to_selection: e.metaKey || e.ctrlKey,
        range_selection: lastSelectedId && e.shiftKey,
        initial_selection_count: initialSelectionCount,
        final_selection_count: finalSelectionCount,
      });
    }
  };

  const handleDragStart = (id: string, e: React.DragEvent) => {
    e.stopPropagation();

    // If the item isn't already selected and isn't being processed
    if (
      !selectedIds.includes(id) &&
      !isInProgress.has(id) &&
      !isRemoving.has(id)
    ) {
      setSelectedIds([id]);
    }

    // Get the IDs to transfer - either the selected IDs or at least the current ID
    const idsToTransfer = selectedIds.includes(id) ? selectedIds : [id];

    // Find the element being dragged
    const draggedElement = e.currentTarget as HTMLElement;

    // Store original border style and remove it temporarily
    draggedElement.style.position = "relative";
    // If multiple items are selected, add a temporary count badge
    if (selectedIds.length > 1) {
      // this has some artifacting when the current element is a table row
      // so we need to check if the element is a table row and if so, compensate for the badge
      const countBadge = document.createElement("div");
      countBadge.style.position = "absolute";
      countBadge.style.top = "-8px";
      countBadge.style.left = "-8px";
      countBadge.style.background = "rgba(51, 33, 218, 1)";
      countBadge.style.color = "white";
      countBadge.style.padding = "2px 8px";
      countBadge.style.borderRadius = "12px";
      countBadge.style.fontSize = "14px";
      countBadge.style.fontWeight = "500";
      countBadge.style.zIndex = "9999";
      countBadge.textContent = `${selectedIds.length}`;
      draggedElement.appendChild(countBadge);

      // Remove the badge and restore border after the drag operation
      requestAnimationFrame(() => {
        draggedElement.removeChild(countBadge);
      });
    }
    e.dataTransfer.setDragImage(draggedElement, 0, 0);
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify(
        idsToTransfer.map((id) => ({
          _id: id,
          isFolder: mergedFoldersAndTickets.find((t) => t._id === id)?.isFolder,
        })),
      ),
    );
    e.dataTransfer.effectAllowed = "move";
    setIsDraggingCards(true);
  };

  const handleDragEnter = (id: string, e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      !selectedIds.includes(id) &&
      !isInProgress.has(id) &&
      !isRemoving.has(id) &&
      isDraggingCards
    ) {
      const ticket = mergedFoldersAndTickets.find((t) => t._id === id);
      // Allow dropping any number of cards into a folder, or a single ticket into another ticket
      if (
        ticket?.isFolder ||
        (selectedIds.length === 1 &&
          !mergedFoldersAndTickets.find((t) => t._id === selectedIds[0])
            ?.isFolder)
      ) {
        setDropTargetId(id);
      }
    }
  };

  const handleDragLeave = (id: string, e: React.DragEvent) => {
    e.preventDefault();
    if (
      dropTargetId === id &&
      !e.currentTarget.contains(e.relatedTarget as Node)
    ) {
      setDropTargetId(undefined);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (id: string, e: React.DragEvent) => {
    e.preventDefault();

    if (
      isDraggingCards &&
      selectedIds.length > 0 &&
      !selectedIds.includes(id) &&
      !isInProgress.has(id) &&
      !isRemoving.has(id)
    ) {
      const draggedIds = [...selectedIds];
      setSelectedIds([]);
      setIsInProgress((prev) => new Set(Array.from(prev).concat(id)));
      setIsRemoving((prev) => new Set(Array.from(prev).concat(draggedIds)));
      handleDropCards(draggedIds, id).finally(() => {
        setIsInProgress((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setIsRemoving((prev) => {
          const next = new Set(prev);
          draggedIds.forEach((id) => next.delete(id));
          return next;
        });
        // Force an immediate UI update by invalidating the router
        router.invalidate();
      });
    }
    setIsDraggingCards(false);
    setDropTargetId(undefined);
  };

  const handleUploadFiles = useCallback(
    async (
      files: FileList | File[] | null,
      ticketId?: string,
      revisionName?: string,
      isFolder?: boolean,
    ) => {
      const startTime = performance.now();
      if (files == null || !files.length) {
        console.warn("Attempted to upload files, but none were provided.");
        return;
      }

      const fileList = Array.from(files);
      const filteredFiles = fileList.filter(
        (file) => !DISALLOWED_FILES.includes(file.name),
      );

      let fileSizeTooBig = false;
      filteredFiles.forEach((file) => {
        if (file.size > FILE_SIZE_LIMITS.UPLOAD.MAX_SIZE_BYTES) {
          fileSizeTooBig = true;
          notify({
            title: "File too large",
            message: `The file ${file.name} is too large to upload. The maximum file size is ${formatFileSize(FILE_SIZE_LIMITS.UPLOAD.MAX_SIZE_BYTES)}.`,
            variant: "error",
          });
          return;
        }
      });

      if (fileSizeTooBig) {
        return;
      }

      const initialFile = filteredFiles[0];
      const ticketStubs = filteredFiles.map(fileToTicketStub);

      if (!ticketId) {
        dispatch(addTemporaryTickets(ticketStubs));
      }

      // Start upload progress tracking
      startUpload(initialFile.name, initialFile.size);

      try {
        const payloadsStartTime = performance.now();
        const payloads = await Promise.all(
          filteredFiles.map(async (file, index) => {
            const payload = await uploadFile(
              file,
              {
                onProgress: (progress) => {
                  updateProgress(progress);
                },
                abortSignal: uploadState.abortController?.signal,
              },
              true,
            );

            const f = file;
            const payload2: {
              title: string;
              folder?: string;
              markup?: string;
            } = {
              title: f.name,
            };

            if (isFolder && ticketId) {
              payload2["folder"] = ticketId;
            } else if (folderId != null) {
              payload2["folder"] = folderId;
            }
            // Create the ticket
            const ticket = await ticketMutation.mutateAsync(payload2);
            // Create the ticket's first revision
            await revisionMutation.mutateAsync({
              ticketId: ticket._id,
              payload: {
                name: "Version 1",
                documentImportType: f.name
                  .slice(f.name.lastIndexOf(".") + 1)
                  .toUpperCase(),
                reviewFiles: [
                  {
                    source: `https://static.puntt.ai/${payload.key}`,
                    type: f.type.includes("image")
                      ? "image"
                      : f.type.includes("video")
                        ? "video"
                        : "file",
                  },
                ],
              },
            });

            dispatch(
              replaceTemporaryTicket({
                temporaryId: ticketStubs[index]._id,
                ticket,
              }),
            );

            return payload;
          }),
        );
        const payloadsDuration = performance.now() - payloadsStartTime;

        const assetsStartTime = performance.now();
        const _assets = await Promise.all(
          payloads.map(async (payload, i) => {
            const key = payload.key;

            return {
              source: `https://static.puntt.ai/${key}`,
              // @ts-expect-error TS1355: we know the type
              type: (filteredFiles[i].type.includes("image")
                ? "image"
                : filteredFiles[i].type.includes("video")
                  ? "video"
                  : "file") as const,
            };
          }),
        );
        const assetsDuration = performance.now() - assetsStartTime;

        // Handle case where we're adding to an existing ticket (not creating new ones)
        if (ticketId != null && !isFolder) {
          // Just add a new revision since we're adding to an existing ticket
          await revisionMutation.mutateAsync({
            ticketId: ticketId,
            payload: {
              name: revisionName ?? "Version 1",
              documentImportType: initialFile.name
                .slice(initialFile.name.lastIndexOf(".") + 1)
                .toLocaleUpperCase(),
              reviewFiles: payloads.map((payload) => ({
                source: `https://static.puntt.ai/${payload.key}`,
                type: initialFile.type.includes("image")
                  ? "image"
                  : initialFile.type.includes("video")
                    ? "video"
                    : "file",
              })),
            },
          });
        }

        const createTicketDuration = performance.now() - payloadsStartTime;

        if (filesUploadRef.current) filesUploadRef.current.files = null;

        router.invalidate();

        const totalDuration = performance.now() - startTime;
        posthog.capture("add_file_as_ticket", {
          file_count: filteredFiles.length,
          ticket_id: ticketId,
          is_folder: isFolder,
          revision_name: revisionName ?? "Version 1",
          file_names: filteredFiles.map((file) => file.name),
          unique_file_types: Array.from(
            new Set(filteredFiles.map((file) => file.type)),
          ),
          first_file_type: filteredFiles[0]?.type,
          total_duration: totalDuration,
          payloads_duration: payloadsDuration,
          assets_duration: assetsDuration,
          create_ticket_duration: createTicketDuration,
        });

        completeUpload();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Upload failed";
        setError(errorMessage);
        console.error("Upload failed:", {
          error,
          errorMessage,
          errorStack: error instanceof Error ? error.stack : undefined,
          fileName: filteredFiles.map((f) => f.name),
          fileSize: filteredFiles.map((f) => f.size),
        });

        // Log to posthog for debugging
        posthog.capture("file_upload_error", {
          errorMessage,
          errorName: error instanceof Error ? error.name : "Unknown",
          fileCount: filteredFiles.length,
          fileNames: filteredFiles.map((f) => f.name),
          fileSizes: filteredFiles.map((f) => f.size),
          totalSize: filteredFiles.reduce((acc, f) => acc + f.size, 0),
          folderId,
          ticketId,
        });
      }
    },
    [
      router,
      folderId,
      startUpload,
      updateProgress,
      setError,
      completeUpload,
      uploadState,
      posthog,
    ],
  );

  const handleUploadFolder = useCallback(
    async ({
      directoryName,
      files,
      parentFolderId = folderId,
    }: {
      directoryName: string;
      files: FileList | File[];
      parentFolderId?: string;
    }) => {
      const startTime = performance.now();

      // Create the folder stub
      const folderStub = directoryNameToFolderStub(
        directoryName,
        parentFolderId,
      );

      // Only create temporary folder if this is a root-level folder (no parent)
      if (!parentFolderId || parentFolderId === folderId) {
        dispatch(addTemporaryFolders([folderStub]));
      }

      const filteredFiles = Array.from(files).filter(
        (f) => !f.name.startsWith("."),
      );

      // Start upload progress tracking for the first file
      if (filteredFiles.length > 0) {
        startUpload(filteredFiles[0].name, filteredFiles[0].size);
      }

      try {
        const payloadStartTime = performance.now();
        const payloads = await Promise.all(
          filteredFiles.map((file) =>
            uploadFile(
              file,
              {
                onProgress: (progress) => {
                  updateProgress(progress);
                },
                abortSignal: uploadState.abortController?.signal,
              },
              true,
            ),
          ),
        );
        const payloadsDuration = performance.now() - payloadStartTime;

        const assetsStartTime = performance.now();
        const assets = await Promise.all(
          payloads.map(async (payload, i) => {
            const key = payload.key;

            return {
              source: `https://static.puntt.ai/${key}`,
              // @ts-expect-error TS1355: we know the type
              type: (filteredFiles[i].type.includes("image")
                ? "image"
                : filteredFiles[i].type.includes("video")
                  ? "video"
                  : "file") as const,
            };
          }),
        );
        const assetsDuration = performance.now() - assetsStartTime;

        const createFolderStartTime = performance.now();
        const createdFolder = await createFolderMutation.mutateAsync({
          folder: {
            folderId: parentFolderId,
            name: directoryName,
          },
          tickets: [],
        });

        // Only dispatch folder replacement if this was a root-level folder
        if (!parentFolderId || parentFolderId === folderId) {
          dispatch(
            replaceTemporaryFolder({
              temporaryId: folderStub._id,
              folder: Object.assign(
                {
                  _id: createdFolder.folder._id!,
                  createdAt: createdFolder.folder
                    .createdAt as unknown as string,
                  updatedAt: createdFolder.folder
                    .updatedAt as unknown as string,
                  // These will get updated shortly due to the folders and tree queries being refetched
                  // but they must be present here or the types will not match
                  thumbnails: [],
                  totalFiles: 0,
                  participants: [],
                  isAiReviewed: false,
                  aiPersonas: [],
                },
                createdFolder.folder,
              ),
            }),
          );
        }

        await Promise.all(
          filteredFiles.map(async (f, i) => {
            const ticket = await ticketMutation.mutateAsync({
              title: f.name,
              folder: createdFolder.folder._id,
            });
            const ticketId = ticket._id;

            await revisionMutation.mutateAsync({
              ticketId: ticketId,
              payload: {
                name: "Version 1",
                documentImportType: f.name
                  .slice(f.name.lastIndexOf(".") + 1)
                  .toUpperCase(),
                reviewFiles: [assets[i]],
              },
            });
          }),
        );
        const createFolderDuration = performance.now() - createFolderStartTime;

        if (filesUploadRef.current) filesUploadRef.current.files = null;

        router.invalidate();

        const totalDuration = performance.now() - startTime;
        posthog.capture("folder_upload_attempt", {
          directory_name: directoryName,
          file_count: filteredFiles.length,
          file_names: filteredFiles.map((file) => file.name),
          unique_file_types: Array.from(
            new Set(filteredFiles.map((file) => file.type)),
          ),
          first_file_type: filteredFiles[0]?.type,
          total_duration: totalDuration,
          payloads_duration: payloadsDuration,
          assets_duration: assetsDuration,
          create_folder_and_tickets_duration: createFolderDuration,
        });

        // Complete upload progress
        completeUpload();
        return createdFolder;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Upload failed";
        setError(errorMessage);
        console.error("Folder upload failed:", {
          error,
          errorMessage,
          errorStack: error instanceof Error ? error.stack : undefined,
          directoryName,
          fileCount: filteredFiles.length,
        });

        // Log to posthog for debugging
        posthog.capture("folder_upload_error", {
          errorMessage,
          errorName: error instanceof Error ? error.name : "Unknown",
          directoryName,
          fileCount: filteredFiles.length,
          fileNames: filteredFiles.map((f) => f.name),
          fileSizes: filteredFiles.map((f) => f.size),
          totalSize: filteredFiles.reduce((acc, f) => acc + f.size, 0),
          parentFolderId,
        });
        throw error;
      }
    },
    [
      router,
      folderId,
      startUpload,
      updateProgress,
      setError,
      completeUpload,
      uploadState,
      posthog,
    ],
  );

  useEffect(() => {
    const handleDragOver = (event: DragEvent) => {
      event.preventDefault();
      const isExternalDrag =
        event.dataTransfer?.types?.includes("Files") ?? false;
      if (event.dataTransfer?.items?.length && isExternalDrag) {
        setIsDragging(true);
      }
    };

    const handleDrop = async (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);
      if (!canUploadTickets()) return;
      setSelectedIds([]);
      posthog.capture("file_drop", {
        message: "File dropped into the window",
        dragged_items: event.dataTransfer?.items?.length ?? 0,
      });

      const items = event.dataTransfer?.items;
      if (!items) return;

      // Track all files and directories being processed
      const directFiles: File[] = [];
      const directories = new Map<
        string,
        {
          name: string;
          fullPath: string;
          parentPath: string | null;
          files: File[];
        }
      >();

      // Process all items first to separate files and directories
      for (const item of Array.from(items)) {
        const entry = item.webkitGetAsEntry();

        if (!entry) {
          const file = item.getAsFile();
          if (file) directFiles.push(file);
          continue;
        }

        if (entry.isFile) {
          const file = item.getAsFile();
          if (file) directFiles.push(file);
        } else if (entry.isDirectory) {
          await readEntry(entry, "", directories);
        }
      }

      // Handle direct files first if there are any
      if (directFiles.length > 0) {
        try {
          await handleUploadFiles(directFiles);
        } catch (error) {
          notify({
            title: "Error uploading files",
            message: `An error occurred while uploading the files: ${await errorMessage(error)}`,
            timeout: 10_000,
            variant: "error",
          });
        }
      }

      // Sort directories by path depth
      const sortedDirs = Array.from(directories.values()).sort((a, b) => {
        return (
          (a.fullPath.match(/\//g) || []).length -
          (b.fullPath.match(/\//g) || []).length
        );
      });

      // Track created folders by their full path
      const folderMap = new Map<string, string>();

      // Process folders in hierarchical order
      for (const dir of sortedDirs) {
        const parentFolderId = dir.parentPath
          ? folderMap.get(dir.parentPath)
          : folderId;

        try {
          const result = await handleUploadFolder({
            directoryName: dir.name,
            files: dir.files,
            parentFolderId,
          });

          if (result?.folder?._id) {
            folderMap.set(dir.fullPath, result.folder._id);
          }
        } catch (error) {
          notify({
            title: "Error uploading folder",
            message: `An error occurred uploading folder ${dir.name}: ${await errorMessage(error)}`,
            timeout: 10_000,
            variant: "error",
          });
        }
      }
    };
    const handleDragLeave = (event: DragEvent) => {
      // If event.relatedTarget is null, it means the drag has left the window
      if (!event.relatedTarget) {
        setIsDragging(false);
        posthog.capture("drag_leave", {
          message: "Drag left the window",
          draggedItems: event.dataTransfer?.items?.length ?? 0,
        });
      }
    };
    // Helper function to read directory entries recursively
    const readEntry = async (
      entry: FileSystemEntry,
      parentPath = "",
      directories: Map<
        string,
        {
          name: string;
          fullPath: string;
          parentPath: string | null;
          files: File[];
        }
      >,
    ): Promise<void> => {
      if (!entry) return;

      return new Promise<void>((resolve) => {
        if (entry.isFile) {
          (entry as FileSystemFileEntry).file((file: File) => {
            if (!file.name.startsWith(".") && parentPath) {
              const dir = directories.get(parentPath);
              if (dir) {
                dir.files.push(file);
              }
            }
            resolve();
          });
        } else if (entry.isDirectory) {
          const reader = (entry as FileSystemDirectoryEntry).createReader();
          const dirName = entry.name;
          const currentPath = parentPath ? `${parentPath}/${dirName}` : dirName;

          // Create directory entry
          directories.set(currentPath, {
            name: dirName,
            fullPath: currentPath,
            parentPath: parentPath || null,
            files: [],
          });

          const readNextBatch = () => {
            reader.readEntries(async (entries: FileSystemEntry[]) => {
              if (!entries.length) {
                resolve();
                return;
              }

              try {
                await Promise.all(
                  entries.map((entry) =>
                    readEntry(entry, currentPath, directories),
                  ),
                );
                readNextBatch(); // Continue reading if there are more entries
              } catch (error) {
                console.error("Error reading entries:", error);
                resolve();
              }
            });
          };

          readNextBatch();
        } else {
          resolve();
        }
      });
    };

    // Attach drag events globally
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);

    return () => {
      // Cleanup event listeners when the component is unmounted
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
    };
  }, [handleUploadFiles]);

  useEffect(() => {
    setIsRemoving(new Set());
  }, [mergedFoldersAndTickets]);

  // Add keyboard event handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        setIsModifierKeyPressed(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) {
        setIsModifierKeyPressed(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  function handleOpenTicket(ticketId: string) {
    navigate({
      to: ticketRoute.to,
      params: { ticketId },
    });
  }

  function handleOpenFolder(folderId: string) {
    navigate({
      search(prev) {
        return {
          ...prev,
          folderId,
        };
      },
    });
  }

  function handleShareTicket(
    ticketId: string,
    emails: string[],
    shareVersion: ShareTicketBody["version"],
  ) {
    shareTicketMutation.mutate(
      { ticketId, emails, version: shareVersion },
      {
        onSuccess() {
          setShareLink(undefined);
          setShareItem(undefined);
          notify({
            title: "Ticket shared",
            message: `Email has been sent to ${emails.length} recipient(s)`,
          });
        },
      },
    );
  }

  function handleShareFolder(folderId: string, emails: string[]) {
    const startTime = performance.now();
    shareFolderMutation.mutate(
      { folderId, emails },
      {
        onSuccess() {
          const folder = folders.find((f) => f._id === folderId);

          posthog.capture("emailed_folder_share_link", {
            folder_id: folderId,
            folder_name: folder?.name,
            emails,
            num_shares: emails.length,
            duration: performance.now() - startTime,
          });

          setShareLink(undefined);
          setShareItem(undefined);
          notify({
            title: "Folder shared",
            message: `Email has been sent to ${emails.length} recipient(s)`,
          });
        },
      },
    );
  }

  function handleDownloadFolder(folderId: string) {
    const startTime = performance.now();
    folderDownloadMutation.mutate(folderId, {
      onSuccess(data) {
        const folder = folders.find((f) => f._id === folderId);

        posthog.capture("download_folder", {
          folder_id: folderId,
          folder_name: folder?.name,
          data_length: data?.url?.length,
          request_duration: performance.now() - startTime,
        });
        if (data) {
          downloadAsset(data.url);
        }
      },
    });
  }

  function handleDownloadTicket(ticketId: string) {
    const startTime = performance.now();
    ticketDownloadMutation.mutate(
      { ticketId },
      {
        onSuccess(data) {
          const ticket = tickets.find((t) => t._id === ticketId);

          posthog.capture("download_ticket", {
            ticket_id: ticketId,
            request_duration: performance.now() - startTime,
            created_hours_ago: ticket?.createdAt
              ? (Date.now() - new Date(ticket.createdAt).getTime()) /
                (1000 * 60 * 60)
              : undefined,
            num_revisions: ticket?.totalRevisions,
            data_length: data?.url?.length,
          });
          if (data) {
            downloadAsset(data.url);
          }
        },
      },
    );
  }

  async function handleDropCards(draggedIds: string[], dropTargetId: string) {
    const dropTarget = mergedFoldersAndTickets.find(
      (t) => t._id === dropTargetId,
    );

    // Move selected tickets/folders to target folder
    if (dropTarget?.isFolder) {
      if (isPunttGuest()) {
        notify({
          title: "Changing file location requires logging in",
          message: "Please log in to move items into a folder.",
        });
        return;
      }

      const startTime = performance.now();
      try {
        await batch(
          draggedIds,
          1,
          async ([dragTargetId]) => {
            const dragTarget = mergedFoldersAndTickets.find(
              (t) => t._id === dragTargetId,
            );
            if (dragTarget?.isFolder) {
              await updateFolderMutation.mutateAsync({
                _id: dragTargetId,
                parentFolder: dropTargetId,
              });
            } else {
              await editProjectMutation.mutateAsync({
                _id: dragTargetId,
                folder: dropTargetId,
              });
            }
          },
          5,
        );
        posthog.capture("tickets_dragged_to_folder", {
          folder_id: dropTargetId,
          num_dropped: draggedIds.length,
          dropped_ids: draggedIds,
          duration_ms: performance.now() - startTime,
        });
      } catch (error) {
        notify({
          title: "Error dragging to folder",
          message: error instanceof Error ? error.message : String(error),
        });
        posthog.capture("tickets_dragged_to_folder_error", {
          folder_id: dropTargetId,
          num_dropped: draggedIds.length,
          dropped_ids: draggedIds,
          duration_ms: performance.now() - startTime,
          ...errorAnalyticsPayload(error),
        });
        // Clear the isRemoving state on error
        setIsRemoving((prev) => {
          draggedIds.forEach((id) => prev.delete(id));
          return new Set(prev);
        });
        throw error; // Re-throw the error to be caught by the caller
      }
    }
    // If selectedIds contains exactly one ticket and dropTargetId is also a ticket, combine the selected ticket with the target ticket
    else if (draggedIds.length === 1) {
      const dragTargetId = draggedIds[0];
      const dragTarget = mergedFoldersAndTickets.find(
        (t) => t._id === dragTargetId,
      );
      if (!dragTarget?.isFolder) {
        if (isPunttGuest() || !canUploadTickets()) {
          notify({
            title: "You don't have permission to add a new version",
            message: "Please contact an administrator for access.",
          });
          return;
        }

        const startTime = performance.now();
        await combineTicketsMutation.mutateAsync(
          {
            payload: {
              receivingTicketId: dropTargetId,
              addingTicketId: dragTargetId,
            },
          },
          {
            onSuccess() {
              router.invalidate();
              posthog.capture("combined_tickets", {
                drop_target_id: dropTargetId,
                drag_target_id: dragTargetId,
                duration_ms: performance.now() - startTime,
              });
            },
            onError(error) {
              notify({
                title: "Error adding version to ticket",
                message: error.message,
              });
              posthog.capture("combined_tickets_error", {
                drop_target_id: dropTargetId,
                drag_target_id: dragTargetId,
                duration_ms: performance.now() - startTime,
                ...errorAnalyticsPayload(error),
              });
            },
          },
        );
      }
    }
  }

  function handleManageTicketVersions(ticketId: string) {
    setTicketId(ticketId);
    setVersionManagerDialogOpen(true);

    getTicketMutation.mutate(ticketId, {
      onSuccess(data) {
        if (isNil(data)) return;

        dispatch(setTicket(data));
      },
    });
  }

  function renderTickets() {
    if (mergedFoldersAndTickets.length === 0 && didSearch) {
      return <NoTicketsFoundView />;
    } else if (mergedFoldersAndTickets.length === 0) {
      return (
        <EmptyTicketsView
          onFilesUpload={() => filesUploadRef.current?.click()}
        />
      );
    }

    switch (view) {
      case "list":
        return (
          <TicketTable
            onDeleteTicket={(ticketId: string) => setDeletingTicketId(ticketId)}
            onTicketClick={handleOpenTicket}
            onFolderClick={handleOpenFolder}
            onDeleteFolder={setDeletingFolderId}
            onRenameFolder={setRenamingId}
            onShareTicket={(ticketId: string) => {
              setShareLink(undefined);
              setShareItem({ id: ticketId, type: "ticket" });
              ticketShareLinkMutation.mutate(
                { ticketId, version: "all" },
                {
                  onSuccess(data) {
                    if (isNil(data)) {
                      throw new Error("An unknown error occurred");
                    }

                    setShareLink(data.url);
                  },
                },
              );
            }}
            onShareFolder={(folderId: string) => {
              setShareLink(undefined);
              setShareItem({ id: folderId, type: "folder" });
              folderShareLinkMutation.mutate(folderId, {
                onSuccess(data) {
                  if (data == null) {
                    throw new Error("An unknown error occurred");
                  }

                  setShareLink(data.url);
                },
              });
            }}
            onDownloadFolder={handleDownloadFolder}
            onDownloadTicket={handleDownloadTicket}
            onManageTicketVersions={handleManageTicketVersions}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            setIsDraggingCards={setIsDraggingCards}
            setDropTargetId={setDropTargetId}
            setLastSelectedId={setLastSelectedId}
            lastSelectedId={lastSelectedId}
            isDraggingCards={isDraggingCards}
            isInProgress={isInProgress}
            isRemoving={isRemoving}
            dropTargetId={dropTargetId}
            isModifierKeyPressed={isModifierKeyPressed}
            onSelect={handleSelect}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            uploadState={uploadState}
            onCancelUpload={cancelUpload}
          />
        );
      case "grid":
        return (
          <TicketGrid
            onDeleteTicket={(ticketId: string) => setDeletingTicketId(ticketId)}
            onTicketCardClick={handleOpenTicket}
            onFolderCardClick={handleOpenFolder}
            revisionMutation={revisionMutation}
            onDeleteFolder={setDeletingFolderId}
            onRenameFolder={setRenamingId}
            onShareTicket={(ticketId: string) => {
              setShareLink(undefined);
              setShareItem({ id: ticketId, type: "ticket" });
              ticketShareLinkMutation.mutate(
                { ticketId, version: "all" },
                {
                  onSuccess(data) {
                    if (data == null) {
                      throw new Error("An unknown error occurred");
                    }

                    setShareLink(data.url);
                  },
                },
              );
            }}
            onShareFolder={(folderId: string) => {
              setShareLink(undefined);
              setShareItem({ id: folderId, type: "folder" });
              folderShareLinkMutation.mutate(folderId, {
                onSuccess(data) {
                  if (data == null) {
                    throw new Error("An unknown error occurred");
                  }

                  setShareLink(data.url);
                },
              });
            }}
            onDownloadFolder={handleDownloadFolder}
            onDownloadTicket={handleDownloadTicket}
            onDropCards={handleDropCards}
            onManageTicketVersions={handleManageTicketVersions}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            setIsDraggingCards={setIsDraggingCards}
            setDropTargetId={setDropTargetId}
            setLastSelectedId={setLastSelectedId}
            lastSelectedId={lastSelectedId}
            isDraggingCards={isDraggingCards}
            isInProgress={isInProgress}
            isRemoving={isRemoving}
            dropTargetId={dropTargetId}
            isModifierKeyPressed={isModifierKeyPressed}
            onSelect={handleSelect}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            uploadState={uploadState}
            onCancelUpload={cancelUpload}
          />
        );
      case "workflow":
        return (
          <TicketGrid
            onDeleteTicket={(ticketId: string) => setDeletingTicketId(ticketId)}
            onTicketCardClick={handleOpenTicket}
            onFolderCardClick={handleOpenFolder}
            revisionMutation={revisionMutation}
            onDeleteFolder={setDeletingFolderId}
            onRenameFolder={setRenamingId}
            onShareTicket={(ticketId: string) => {
              setShareLink(undefined);
              setShareItem({ id: ticketId, type: "ticket" });
              ticketShareLinkMutation.mutate(
                { ticketId, version: "all" },
                {
                  onSuccess(data) {
                    if (data == null) {
                      throw new Error("An unknown error occurred");
                    }

                    setShareLink(data.url);
                  },
                },
              );
            }}
            onShareFolder={(folderId: string) => {
              setShareLink(undefined);
              setShareItem({ id: folderId, type: "folder" });
              folderShareLinkMutation.mutate(folderId, {
                onSuccess(data) {
                  if (data == null) {
                    throw new Error("An unknown error occurred");
                  }

                  setShareLink(data.url);
                },
              });
            }}
            onDownloadFolder={handleDownloadFolder}
            onDownloadTicket={handleDownloadTicket}
            onDropCards={handleDropCards}
            onManageTicketVersions={handleManageTicketVersions}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            setIsDraggingCards={setIsDraggingCards}
            setDropTargetId={setDropTargetId}
            setLastSelectedId={setLastSelectedId}
            lastSelectedId={lastSelectedId}
            isDraggingCards={isDraggingCards}
            isInProgress={isInProgress}
            isRemoving={isRemoving}
            dropTargetId={dropTargetId}
            isModifierKeyPressed={isModifierKeyPressed}
            onSelect={handleSelect}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            uploadState={uploadState}
            onCancelUpload={cancelUpload}
          />
        );
      default:
        throw new Error("Unsupported view type");
    }
  }

  function handleSearchInput(hasUserSearched: boolean) {
    setDidSearch(hasUserSearched);
  }

  useEffect(() => {
    if (!versionManagerDialogOpen) {
      dispatch(resetTicket());
      router.invalidate();
    }
  }, [versionManagerDialogOpen]);

  const versionManagerList =
    versions.map((board, i) => ({
      _id: board._id,
      title: board.name ?? `Version ${i + 1}`,
      thumbnail: board.thumbnails[0] ?? board.screenshotUrl,
    })) ?? [];

  return (
    <div
      className="flex h-full flex-col overflow-hidden"
      data-testid="tickets-page"
    >
      <CreateFolderDialog
        open={isDialogOpen}
        onOpenChange={setDialogOpen}
        folderId={folderId}
      />
      <PageHeader className="w-full">
        <TicketsHeader
          isPending={isLoading}
          onSearch={handleSearchInput}
          onFilesUpload={() => filesUploadRef.current?.click()}
          onFolderUpload={() => foldersUploadRef.current?.click()}
          onNewFolder={() => setDialogOpen(true)}
          onDeleteFolder={setDeletingFolderId}
          onRenameFolder={setRenamingId}
          onShareFolder={(folderId: string) => {
            setShareLink(undefined);
            setShareItem({ id: folderId, type: "folder" });
            folderShareLinkMutation.mutate(folderId, {
              onSuccess(data) {
                if (data == null) {
                  throw new Error("An unknown error occurred");
                }

                setShareLink(data.url);
              },
            });
          }}
          onDownloadFolder={handleDownloadFolder}
        />
      </PageHeader>
      <article
        ref={articleRef}
        className="relative grid flex-1 content-start gap-6 overflow-auto py-6"
      >
        {/* This is used in both the no tickets view and the "File Upload" option in the "New" dropdown */}
        <input
          data-testid="tickets-files-upload-input"
          type="file"
          ref={filesUploadRef}
          className="hidden"
          multiple
          onChange={({ target }) => handleUploadFiles(target.files)}
        />
        <input
          data-testid="tickets-folder-upload-input"
          type="file"
          ref={foldersUploadRef}
          className="hidden"
          // @ts-expect-error TS(2322): We have this type
          webkitdirectory="true"
          // eslint-disable-next-line react/no-unknown-property
          mozdirectory="true"
          // eslint-disable-next-line react/no-unknown-property
          directory="true"
          onChange={async (event) => {
            const files = Array.from(event.target.files || []);
            if (!files.length) return;

            // Group files by their full directory path
            const directories = new Map<
              string,
              {
                name: string;
                fullPath: string;
                parentPath: string | null;
                files: File[];
              }
            >();

            files.forEach((file) => {
              const fullPath = file.webkitRelativePath;
              const parts = fullPath.split("/");
              // Remove the file name
              parts.pop();

              // Process each directory level in the path
              parts.reduce((parentPath, dirName, index) => {
                const currentPath = parentPath
                  ? `${parentPath}/${dirName}`
                  : dirName;

                if (!directories.has(currentPath)) {
                  directories.set(currentPath, {
                    name: dirName,
                    fullPath: currentPath,
                    parentPath: index === 0 ? null : parentPath,
                    files: [],
                  });
                }

                // If this is the immediate parent directory of the file, add the file
                if (index === parts.length - 1) {
                  const dir = directories.get(currentPath);
                  if (dir) {
                    dir.files.push(file);
                  }
                }

                return currentPath;
              }, "");
            });

            // Sort by path depth to ensure parents are created before children
            const sortedDirs = Array.from(directories.values()).sort((a, b) => {
              return (
                (a.fullPath.match(/\//g) || []).length -
                (b.fullPath.match(/\//g) || []).length
              );
            });

            // Track created folders by their full path
            const folderMap = new Map<string, string>();

            for (const dir of sortedDirs) {
              const parentFolderId = dir.parentPath
                ? folderMap.get(dir.parentPath)
                : folderId;

              try {
                const result = await handleUploadFolder({
                  directoryName: dir.name,
                  files: dir.files,
                  parentFolderId,
                });

                if (result?.folder?._id) {
                  folderMap.set(dir.fullPath, result.folder._id);
                }
              } catch (error) {
                notify({
                  title: "Error uploading folder",
                  message: `An error occurred uploading folder ${dir.name}: ${await errorMessage(error)}`,
                  timeout: 10_000,
                  variant: "error",
                });
              }
            }
          }}
        />
        {isLoading ? (
          <Grid gap="4" className="mt-40 text-center">
            <Heading size="5">{t("tickets.view.loadingDocuments")}</Heading>
          </Grid>
        ) : (
          <>
            {renderTickets()}
            <InfiniteScrollTrigger
              lastItemIndex={
                mergedFoldersAndTickets[mergedFoldersAndTickets.length - 1]
                  ?.updatedIndex
              }
              hasMore={more}
            />
            <DragAndDropFileUpload isDragging={isDragging} />
          </>
        )}
      </article>

      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <IconButton
          data-testid="view-icon-button-1668"
          size="4"
          className="text-white fixed right-6 top-20 z-50 rounded-full bg-puntt-accent-9 shadow-lg transition-all duration-200 hover:bg-puntt-accent-10"
          onClick={scrollToTop}
          aria-label={t("tickets.view.scrollTop")}
        >
          <ArrowUp size={20} />
        </IconButton>
      )}

      <ShareDialog
        item={shareItem}
        latestVersionNumber={
          shareItem?.type === "ticket"
            ? tickets.find((t) => t._id === shareItem?.id)?.totalRevisions
            : undefined
        }
        shareLink={shareLink}
        shareLinkPending={
          folderShareLinkMutation.isPending || ticketShareLinkMutation.isPending
        }
        isPending={
          shareFolderMutation.isPending || shareTicketMutation.isPending
        }
        onCancel={() => setShareItem(undefined)}
        onShare={(
          emails: string[],
          shareVersion: ShareTicketBody["version"],
        ) => {
          if (shareItem == null) {
            throw new Error("No item to share");
          }

          switch (shareItem.type) {
            case "folder":
              return handleShareFolder(shareItem.id, emails);
            case "ticket":
              return handleShareTicket(shareItem.id, emails, shareVersion);
            default:
              throw new Error("Unknown share item type");
          }
        }}
        onVersionChange={(version) =>
          shareTicketMutation.mutate(
            { ticketId: shareItem?.id ?? "", version },
            {
              onSuccess(data) {
                if (data == null) {
                  throw new Error("An unknown error occurred");
                }

                setShareLink(data.url);
              },
            },
          )
        }
      />
      <RenameDialog
        isPending={
          updateFolderMutation.isPending || editProjectMutation.isPending
        }
        _id={renamingId}
        onCancel={() => setRenamingId(undefined)}
        onSave={(_id, folderName) => {
          const isFolder =
            mergedFoldersAndTickets.find((t) => t._id === _id)?.isFolder ||
            _id === folderId;

          if (isFolder) {
            return updateFolderMutation.mutateAsync(
              { _id, name: folderName },
              {
                onSuccess() {
                  setRenamingId(undefined);
                  posthog.capture("folder_renamed", {
                    folder_id: _id,
                    folder_name: folderName,
                  });
                  dispatch(
                    updateBreadcrumbName({
                      folderId: _id,
                      newName: folderName,
                    }),
                  );
                  return router.invalidate();
                },
              },
            );
          }
          return editProjectMutation.mutateAsync(
            { _id, title: folderName },
            {
              onSuccess() {
                setRenamingId(undefined);
                posthog.capture("folder_renamed", {
                  folder_id: _id,
                  folder_name: folderName,
                });
                dispatch(
                  updateTicketTitle({ ticketId: _id, newTitle: folderName }),
                );
                return router.invalidate();
              },
            },
          );
        }}
      />
      <DeleteTicketDialog
        isPending={mutation.isPending}
        ticketId={deletingTicketId}
        onCancel={() => setDeletingTicketId(undefined)}
        onDelete={() => {
          if (deletingTicketId == null) {
            throw new Error(
              "The ticket we want to delete cannot have a null or undefined ID",
            );
          }

          mutation.mutate(
            { ticketId: deletingTicketId },
            {
              onSuccess() {
                setDeletingTicketId(undefined);
                posthog.capture("ticket_deleted", {
                  ticket_id: deletingTicketId,
                });
                return router.invalidate();
              },
              onError() {
                window.alert("Unable to delete document. Please try again.");
              },
            },
          );
        }}
      />
      <DeleteFolderDialog
        isPending={deleteFolderMutation.isPending}
        folderId={deletingFolderId}
        onCancel={() => setDeletingFolderId(undefined)}
        onDelete={() => {
          if (deletingFolderId == null) {
            throw new Error(
              "The folder we want to delete cannot have a null or undefined ID",
            );
          }

          deleteFolderMutation.mutate(deletingFolderId, {
            onSuccess() {
              if (folderId === deletingFolderId) {
                navigate({
                  search: ({ folderId: _folderId, ...rest }) => rest,
                });
              }
              setDeletingFolderId(undefined);
              posthog.capture("folder_deleted", {
                folder_id: deletingFolderId,
              });
              return router.invalidate();
            },
            onError() {
              window.alert("Unable to delete folder. Please try again.");
            },
          });
        }}
      />
      <VersionManagerDialog
        open={versionManagerDialogOpen}
        onOpenChange={setVersionManagerDialogOpen}
        onDeleteVersion={(revisionId) =>
          deleteRevisionMutation.mutate(revisionId, {
            onSuccess(data) {
              posthog.capture("delete_version", {
                ticketId,
                versionId: revisionId,
              });
              if (isNil(data)) return;

              const { boards } = data;

              if (!boards.length) {
                setVersionManagerDialogOpen(false);
              }
            },
          })
        }
        revisions={versionManagerList}
        isPending={deleteRevisionMutation.isPending}
        deletingId={deleteRevisionMutation.variables}
        isLoadingTickets={getTicketMutation.isPending}
      />
    </div>
  );
}
